class Oscillator {
  constructor(options = {}) {
    this.phase = options.phase || 0;
    this.offset = options.offset || 0;
    this.frequency = options.frequency || 0.001;
    this.amplitude = options.amplitude || 1;
  }

  update() {
    this.phase += this.frequency;
    return this.offset + Math.sin(this.phase) * this.amplitude;
  }
}

class Node {
  constructor(x, y) {
    this.x = x;
    this.y = y;
    this.vx = 0;
    this.vy = 0;
  }
}

export function renderCanvas(canvas) {
  if (!canvas) {
    return () => {};
  }

  const context = canvas.getContext("2d");

  if (!context) {
    return () => {};
  }

  const settings = {
    friction: 0.5,
    trails: 32,
    size: 42,
    dampening: 0.025,
    tension: 0.98,
  };

  const pointer = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  };

  const hue = new Oscillator({
    phase: Math.random() * Math.PI * 2,
    amplitude: 90,
    frequency: 0.0015,
    offset: 210,
  });

  let animationFrame = null;
  let isRunning = true;
  let lines = [];

  class Line {
    constructor(index) {
      this.spring = 0.45 + (index / settings.trails) * 0.025 + Math.random() * 0.02;
      this.friction = settings.friction + Math.random() * 0.01 - 0.005;
      this.nodes = Array.from({ length: settings.size }, () => new Node(pointer.x, pointer.y));
    }

    update() {
      let spring = this.spring;
      let node = this.nodes[0];

      node.vx += (pointer.x - node.x) * spring;
      node.vy += (pointer.y - node.y) * spring;

      for (let index = 0; index < this.nodes.length; index += 1) {
        node = this.nodes[index];

        if (index > 0) {
          const previousNode = this.nodes[index - 1];
          node.vx += (previousNode.x - node.x) * spring;
          node.vy += (previousNode.y - node.y) * spring;
          node.vx += previousNode.vx * settings.dampening;
          node.vy += previousNode.vy * settings.dampening;
        }

        node.vx *= this.friction;
        node.vy *= this.friction;
        node.x += node.vx;
        node.y += node.vy;
        spring *= settings.tension;
      }
    }

    draw() {
      let currentX = this.nodes[0].x;
      let currentY = this.nodes[0].y;

      context.beginPath();
      context.moveTo(currentX, currentY);

      for (let index = 1; index < this.nodes.length - 2; index += 1) {
        const currentNode = this.nodes[index];
        const nextNode = this.nodes[index + 1];
        currentX = (currentNode.x + nextNode.x) * 0.5;
        currentY = (currentNode.y + nextNode.y) * 0.5;
        context.quadraticCurveTo(currentNode.x, currentNode.y, currentX, currentY);
      }

      const penultimateNode = this.nodes[this.nodes.length - 2];
      const lastNode = this.nodes[this.nodes.length - 1];
      context.quadraticCurveTo(penultimateNode.x, penultimateNode.y, lastNode.x, lastNode.y);
      context.stroke();
      context.closePath();
    }
  }

  function resetLines() {
    lines = Array.from({ length: settings.trails }, (_, index) => new Line(index));
  }

  function resizeCanvas() {
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width));
    canvas.height = Math.max(1, Math.floor(rect.height));
  }

  function updatePointer(event) {
    const point = event.touches?.[0] || event;
    pointer.x = point.clientX;
    pointer.y = point.clientY;
  }

  function animate() {
    if (!isRunning) {
      return;
    }

    context.globalCompositeOperation = "source-over";
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.globalCompositeOperation = "lighter";
    context.strokeStyle = `hsla(${Math.round(hue.update())}, 100%, 65%, 0.045)`;
    context.lineWidth = 9;

    lines.forEach((line) => {
      line.update();
      line.draw();
    });

    animationFrame = window.requestAnimationFrame(animate);
  }

  function handleBlur() {
    isRunning = false;

    if (animationFrame) {
      window.cancelAnimationFrame(animationFrame);
    }
  }

  function handleFocus() {
    if (isRunning) {
      return;
    }

    isRunning = true;
    animationFrame = window.requestAnimationFrame(animate);
  }

  resizeCanvas();
  resetLines();
  animationFrame = window.requestAnimationFrame(animate);

  window.addEventListener("resize", resizeCanvas);
  window.addEventListener("mousemove", updatePointer);
  window.addEventListener("touchstart", updatePointer, { passive: true });
  window.addEventListener("touchmove", updatePointer, { passive: true });
  window.addEventListener("blur", handleBlur);
  window.addEventListener("focus", handleFocus);

  return () => {
    isRunning = false;

    if (animationFrame) {
      window.cancelAnimationFrame(animationFrame);
    }

    window.removeEventListener("resize", resizeCanvas);
    window.removeEventListener("mousemove", updatePointer);
    window.removeEventListener("touchstart", updatePointer);
    window.removeEventListener("touchmove", updatePointer);
    window.removeEventListener("blur", handleBlur);
    window.removeEventListener("focus", handleFocus);
  };
}